import { from, Observable, of } from 'rxjs';
import { catchError, concatMap, toArray } from 'rxjs/operators';

export type StockMovementBatchResult<T> = T[] | 'skipped';

const SESSION_STORAGE_KEY = 'erp.stock-batches-applied';

export function stockBatchCacheKey(documentId: number, movementType: string): string {
    return `${documentId}:${normalizeMovementType(movementType)}`;
}

/** Empêche les doublons (double-clic, re-clic statut, rechargement session). */
export class StockBatchIdempotenceStore {
    private readonly appliedMemory = new Set<string>();
    private readonly inFlight = new Set<string>();

    isApplied(documentId: number, movementType: string): boolean {
        const key = stockBatchCacheKey(documentId, movementType);
        return this.appliedMemory.has(key) || this.readSession().has(key);
    }

    isInFlight(documentId: number, movementType: string): boolean {
        return this.inFlight.has(stockBatchCacheKey(documentId, movementType));
    }

    /** Retourne false si déjà appliqué ou un batch est en cours. */
    tryBegin(documentId: number, movementType: string): boolean {
        const key = stockBatchCacheKey(documentId, movementType);
        if (this.appliedMemory.has(key) || this.inFlight.has(key) || this.readSession().has(key)) {
            return false;
        }
        this.inFlight.add(key);
        return true;
    }

    markApplied(documentId: number, movementType: string): void {
        const key = stockBatchCacheKey(documentId, movementType);
        this.inFlight.delete(key);
        this.appliedMemory.add(key);
        const session = this.readSession();
        session.add(key);
        this.writeSession(session);
    }

    markFailed(documentId: number, movementType: string): void {
        this.inFlight.delete(stockBatchCacheKey(documentId, movementType));
    }

    private readSession(): Set<string> {
        try {
            if (typeof sessionStorage === 'undefined') {
                return new Set();
            }
            const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (!raw) {
                return new Set();
            }
            const arr = JSON.parse(raw);
            return new Set(Array.isArray(arr) ? arr : []);
        } catch {
            return new Set();
        }
    }

    private writeSession(set: Set<string>): void {
        try {
            if (typeof sessionStorage === 'undefined') {
                return;
            }
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify([...set]));
        } catch {
            /* quota / mode privé */
        }
    }
}

export function normalizeMovementType(value: unknown): string {
    return String(value ?? '')
        .trim()
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

/** Vérifie si un mouvement API correspond au document (et optionnellement au type). */
export function movementMatchesDocument(
    movement: unknown,
    documentId: number,
    movementType?: string
): boolean {
    if (!movement || typeof movement !== 'object') {
        return false;
    }
    const row = movement as Record<string, unknown>;
    const docId = Number(row['document_id'] ?? row['documentId'] ?? row['doc_id']);
    if (!Number.isFinite(docId) || docId !== documentId) {
        return false;
    }
    if (!movementType) {
        return true;
    }
    const typeRaw = row['type'] ?? row['type_mouvement'] ?? row['mouvement_type'];
    return normalizeMovementType(typeRaw) === normalizeMovementType(movementType);
}

/** Repli si l’API ne renvoie pas document_id mais conserve reference_document. */
export function movementMatchesDocumentRef(
    movement: unknown,
    referenceDocument: string | null | undefined,
    movementType?: string
): boolean {
    const ref = String(referenceDocument ?? '').trim();
    if (!ref || !movement || typeof movement !== 'object') {
        return false;
    }
    const row = movement as Record<string, unknown>;
    const movRef = String(row['reference_document'] ?? row['reference'] ?? '').trim();
    if (!movRef || movRef !== ref) {
        return false;
    }
    if (!movementType) {
        return true;
    }
    const typeRaw = row['type'] ?? row['type_mouvement'] ?? row['mouvement_type'];
    return normalizeMovementType(typeRaw) === normalizeMovementType(movementType);
}

export function movementMatchesDocumentOrRef(
    movement: unknown,
    documentId: number,
    movementType: string | undefined,
    referenceDocument?: string | null
): boolean {
    return (
        movementMatchesDocument(movement, documentId, movementType) ||
        movementMatchesDocumentRef(movement, referenceDocument, movementType)
    );
}

/**
 * Exécute les observables un par un (évite les lectures stock concurrentes
 * sur le même produit / entrepôt).
 */
export function runObservablesSequentially<T>(sources: Observable<T>[]): Observable<T[]> {
    if (!sources.length) {
        return of([]);
    }
    return from(sources).pipe(
        concatMap((source) => source.pipe(catchError((error) => of({ __stockBatchError: error } as unknown as T)))),
        toArray()
    );
}

export function countStockBatchOutcomes(results: unknown[]): { successCount: number; errorCount: number } {
    let successCount = 0;
    let errorCount = 0;
    for (const r of results) {
        if (r && typeof r === 'object' && '__stockBatchError' in (r as object)) {
            errorCount++;
            continue;
        }
        if (r && typeof r === 'object' && 'success' in (r as object)) {
            if ((r as { success: boolean }).success === true) {
                successCount++;
            } else {
                errorCount++;
            }
            continue;
        }
        if (r !== null && r !== undefined) {
            successCount++;
        } else {
            errorCount++;
        }
    }
    return { successCount, errorCount };
}
